import React, { useState, useEffect, useCallback } from "react";
import Select, { components } from "react-select";
import { useController } from "react-hook-form";

import { StyledSelectWrapper, Label } from "./selectLabeled.style";

const Control = (props) => (
  <>
    <Label $isFloating={props.isFocused || props.hasValue}>
      {props.selectProps.label}
    </Label>
    <components.Control {...props} />
  </>
);

const CustomSelectLabeled = ({
  control,
  name,
  options,
  required,
  error,
  placeholder,
  readOnly,
  additionalStyles = {},
  changeCallback = undefined,
}) => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => setLoaded(true), []);

  const { field } = useController({
    name,
    control,
    rules: { required: { value: required, message: "This field is required" } },
  });

  const getInitialSelectedOption = () => {
    const selectedOption = options
      .flatMap((obj) => {
        if (Object.prototype.hasOwnProperty.call(obj, "options"))
          return obj.options;
        return obj;
      })
      .find((option) => option.value === field.value);
    return selectedOption || null;
  };
  const [selectedOption, setSelectedOption] = useState(
    getInitialSelectedOption(),
  );

  const handleChange = (selectedOption) => {
    field.onChange(selectedOption?.value);
    setSelectedOption(selectedOption);
    if (changeCallback) changeCallback(selectedOption);
  };

  return (
    <StyledSelectWrapper
      $error={error ? 1 : 0}
      style={additionalStyles}
      $selected={field?.value}
    >
      {loaded && (
        <Select
          onChange={handleChange}
          onBlur={field.onBlur}
          value={selectedOption}
          name={field.name}
          inputRef={field.ref}
          options={options}
          isSearchable
          isClearable
          isDisabled={readOnly}
          label={placeholder || "Select an option"}
          placeholder=""
          components={{ Control }}
          required={required}
          classNamePrefix="select"
        />
      )}
      <span className="error">{error?.message || ""}</span>
    </StyledSelectWrapper>
  );
};

const CustomSelectLabeledNonForm = ({
  initialValue,
  onChange,
  name,
  options,
  placeholder,
  readOnly,
  additionalStyles = {},
}) => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => setLoaded(true), []);

  const getInitialSelectedOption = useCallback(() => {
    const selectedOption = options
      .flatMap((obj) => {
        if (Object.prototype.hasOwnProperty.call(obj, "options"))
          return obj.options;
        return obj;
      })
      .find((option) => option.value === initialValue);
    return selectedOption || null;
  }, [initialValue, options]);
  const [selectedOption, setSelectedOption] = useState(
    getInitialSelectedOption(),
  );

  useEffect(() => {
    setSelectedOption(getInitialSelectedOption());
  }, [initialValue, getInitialSelectedOption]);

  const handleChange = (selectedOption) => {
    onChange(selectedOption?.value);
    setSelectedOption(selectedOption);
  };

  return (
    <StyledSelectWrapper
      $error={0}
      style={additionalStyles}
      $selected={selectedOption?.value}
    >
      {loaded && (
        <Select
          onChange={handleChange}
          value={selectedOption}
          name={name}
          options={options}
          isSearchable
          isDisabled={readOnly}
          label={placeholder || "Select an option"}
          placeholder=""
          components={{ Control }}
          required={false}
          classNamePrefix="select"
        />
      )}
      <span className="error"></span>
    </StyledSelectWrapper>
  );
};

const SelectField = ({ useInForm = true, ...props }) =>
  useInForm ? (
    <CustomSelectLabeled {...props} />
  ) : (
    <CustomSelectLabeledNonForm {...props} />
  );

export default SelectField;
